import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Shield, Users, Star, Truck, Key, Car, ArrowRight, CheckCircle, FileText, PhoneCall, ChevronRight, Phone, Gauge, Calendar } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, BRAND } from "../App";

const HomePage = () => {
  const [featuredCars, setFeaturedCars] = useState([]);

  useEffect(() => {
    axios.get(`${API}/cars`).then(res => {
      setFeaturedCars(res.data.filter(c => c.status === "available").slice(0, 3));
    }).catch(() => {});
  }, []);

  const formatPrice = (price) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(price);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* HERO SECTION */}
      <section className="relative bg-black overflow-hidden pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1620568797051-7adeb62dbdeb?w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
              <div className="flex items-center gap-2 mb-6">
                <span className="text-white/90 text-xs sm:text-sm uppercase tracking-[0.3em] font-semibold">Vehicle Transportation</span>
                <span className="w-2 h-2 bg-[#E31E24]"></span>
                <span className="text-white/90 text-xs sm:text-sm uppercase tracking-[0.3em] font-semibold">Towing</span>
                <span className="w-2 h-2 bg-[#E31E24]"></span>
                <span className="text-white/90 text-xs sm:text-sm uppercase tracking-[0.3em] font-semibold">Sales</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[0.95] mb-4">
                Reliable Vehicle<br />
                <span className="block">Transportation</span>
                <span className="text-[#E31E24] block">You Can Count On.</span>
              </h1>
              <p className="text-base sm:text-lg text-white/80 mb-8 max-w-xl leading-relaxed">
                From local to long distance, we provide safe, dependable, and professional vehicle transport, towing, and sales for Dealerships, Auctions, Business & Personal needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/get-quote" className="bg-[#E31E24] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#C01A1F] transition-all flex items-center justify-center gap-2 group">
                  Get a Quote / Book Now
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/services" className="bg-white text-black font-bold uppercase tracking-wide px-8 py-4 hover:bg-white/90 transition-all text-center">
                  Our Services
                </Link>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-4 mt-12 pt-8 border-t border-white/20">
                <div className="flex items-start gap-2">
                  <Shield className="text-[#E31E24] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-white font-bold text-xs sm:text-sm">Safe & Insured</p>
                    <p className="text-white/60 text-xs">Transport</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Users className="text-[#E31E24] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-white font-bold text-xs sm:text-sm">Dealerships, Auctions,</p>
                    <p className="text-white/60 text-xs">Business & Personal</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Star className="text-[#E31E24] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-white font-bold text-xs sm:text-sm">Trusted by</p>
                    <p className="text-white/60 text-xs">Many Customers</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="hidden lg:block">
              <img src={BRAND.logoPromo} alt={BRAND.name} className="w-full max-w-lg mx-auto drop-shadow-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* COMPLETE SOLUTION SECTION */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-black">
              A Complete Solution<br />
              <span className="text-[#E31E24]">For All Your Vehicle Needs</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Truck, title: "Transport / Tow", desc: "Local & long distance transport, dealerships, auctions, business & personal.", link: "/services" },
              { icon: Key, title: "Request Service", desc: "Submit your transportation request online. We'll quote and schedule promptly.", link: "/get-quote" },
              { icon: Car, title: "Buy a Vehicle", desc: "Quality pre-owned vehicles at great prices. Browse our current inventory.", link: "/cars-for-sale" },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-black text-white p-8 hover:bg-[#E31E24] transition-all cursor-pointer"
              >
                <Link to={item.link} className="block">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 bg-[#E31E24] group-hover:bg-black flex items-center justify-center transition-colors">
                      <item.icon size={28} className="text-white" />
                    </div>
                    <ArrowRight size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <h3 className="text-2xl font-black uppercase mb-3">{item.title}</h3>
                  <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-16 pt-16 border-t border-black/10">
            <div className="text-center">
              <p className="text-4xl font-black text-[#E31E24] mb-2">1000+</p>
              <p className="text-black/60 text-sm uppercase tracking-wide">Vehicles Transported</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-[#E31E24] mb-2">5-Star</p>
              <p className="text-black/60 text-sm uppercase tracking-wide">Customer Service</p>
            </div>
            <div className="text-center col-span-2 md:col-span-1">
              <p className="text-4xl font-black text-[#E31E24] mb-2">Nationwide</p>
              <p className="text-black/60 text-sm uppercase tracking-wide">Coverage</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3-STEP PROCESS */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E31E24] mb-3">Simple Process</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-black">How It Works</h2>
            <p className="text-black/60 mt-4 max-w-xl mx-auto">Three simple steps to get your vehicle moving.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: "01", title: "Submit Request", desc: "Fill out our simple transportation request form with your vehicle and location details.", icon: FileText },
              { num: "02", title: "Receive Your Quote", desc: "We review your request and send you a competitive quote tailored to your needs.", icon: PhoneCall },
              { num: "03", title: "Confirm Your Transport", desc: "Approve the quote and we'll schedule and complete your transport safely and reliably.", icon: CheckCircle },
            ].map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="relative"
              >
                <div className="bg-white p-8 border-l-4 border-[#E31E24] h-full">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-6xl font-black text-black/10">{step.num}</span>
                    <step.icon size={40} className="text-[#E31E24]" />
                  </div>
                  <h3 className="text-xl font-black uppercase mb-3 text-black">{step.title}</h3>
                  <p className="text-black/70 text-sm leading-relaxed">{step.desc}</p>
                </div>
                {idx < 2 && (
                  <ChevronRight size={32} className="hidden md:block absolute top-1/2 -right-4 -translate-y-1/2 text-[#E31E24] z-10" />
                )}
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/get-quote" className="inline-flex items-center gap-2 bg-[#E31E24] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#C01A1F] transition-all">
              Start Your Request
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="bg-black py-20 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E31E24] mb-3">Who We Serve</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight">
              Trusted By <span className="text-[#E31E24]">Everyone</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Private Owners", desc: "Safe transport for your personal vehicle anywhere you need it." },
              { title: "Dealerships", desc: "Professional vehicle transport solutions for dealers of all sizes." },
              { title: "Auction Buyers", desc: "Fast pickup and delivery from auctions to your location." },
              { title: "Businesses", desc: "Fleet transport and recurring service for business clients." },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/5 p-6 border-t-2 border-[#E31E24] hover:bg-white/10 transition-colors"
              >
                <h3 className="text-xl font-black uppercase mb-3">{item.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CARS FOR SALE PREVIEW */}
      {featuredCars.length > 0 && (
        <section className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-wrap justify-between items-end mb-10 gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E31E24] mb-3">Featured Vehicles</p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-black">Cars for Sale</h2>
              </div>
              <Link to="/cars-for-sale" className="flex items-center gap-2 font-bold text-black hover:text-[#E31E24] transition-colors">
                View All Vehicles <ArrowRight size={20} />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCars.map(car => (
                <Link key={car.id} to="/cars-for-sale" className="group bg-gray-50 border border-black/10 overflow-hidden hover:shadow-xl transition-all">
                  <div className="aspect-video bg-gray-200 overflow-hidden">
                    {car.image_url || (car.images && car.images[0]) ? (
                      <img src={car.image_url || car.images[0]} alt={`${car.year} ${car.make} ${car.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Car size={64} className="text-black/20" /></div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-black uppercase mb-1 text-black">{car.year} {car.make} {car.model}</h3>
                    <p className="text-sm text-black/60 mb-3">{new Intl.NumberFormat("en-US").format(car.mileage)} miles</p>
                    <p className="text-2xl font-black text-[#E31E24] mb-4">{formatPrice(car.price)}</p>
                    <span className="inline-flex items-center gap-2 text-sm font-bold text-black group-hover:text-[#E31E24] transition-colors">
                      View Details <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA SECTION */}
      <section className="bg-[#E31E24] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black uppercase mb-3">Ready to Move Your Vehicle?</h2>
              <p className="text-white/90 text-lg">Call {BRAND.owner} directly or request a quote online.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BRAND.phoneHref} className="bg-black text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-black transition-all flex items-center gap-2">
                <Phone size={20} />
                Call {BRAND.phone}
              </a>
              <Link to="/get-quote" className="bg-white text-black font-bold uppercase tracking-wide px-8 py-4 hover:bg-black hover:text-white transition-all">
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
